import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  AuthService,
  Formats,
  isEditByModerator,
  ModifyRecurringMeetingTypes,
  PageRoutes,
  RepeatTypes,
  setClass,
  TMeetingSetup,
  TRepeatTypes,
  useStore,
  useViewModel,
} from "@utils";
import { isMobile } from "react-device-detect";
import { CalendarViewModel, MeetingPlanerViewModel } from "@views";
import { useNavigate } from "react-router-dom";
import { BigBlueArrowLeft, Cross, DeleteMeetingIcon } from "../../../svgs";
import { MeetingViewModel } from "./MeetingViewModel";
import {
  DateBlock,
  DescriptionBlock,
  FormatBlock,
  IntervalBlock,
  LocationBlock,
  MembersBlock,
  NameBlock,
  MobileDateBlock,
  OuterMembersBlock,
} from "./components";
import { MembersStore, RoomsStore } from "../../../../store";
import { MessageDialogViewModel } from "../../../messageDialog";
import { Button } from "../../../button";
import { MobileMembersSelectList } from "./MobileMembersSelectList";
import { ScheduleViewModel } from "../../../../views/calendarPage/Schedule/ScheduleViewModel";
import "../../../../styles/components/editMeeting.scss";

export const EditMeeting = observer(() => {
  const meetingViewModel = useViewModel(MeetingViewModel);
  const calendarViewModel = useViewModel(CalendarViewModel);
  const meetingPlanerViewModel = useViewModel(MeetingPlanerViewModel);
  const roomsStore = useStore(RoomsStore);
  const membersStore = useViewModel(MembersStore);
  const messageDialogService = useViewModel(MessageDialogViewModel);
  const scheduleViewModel = useViewModel(ScheduleViewModel);

  const navigate = useNavigate();

  const [intervalChanged, setIntervalChanged] = useState(false);
  const [dateChanged, setDateChanged] = useState(false);
  const [showError, setShowError] = useState(false);
  const [outerMembersError, setOuterMembersError] = useState(false);
  const [showMobileMemberSelect, setShowMobileMemberSelect] = useState(false);

  const refreshStatuses = () =>
    membersStore.getMembersStatus({
      startIntervalDate: meetingViewModel.meetingData.startDate,
      endIntervalDate: meetingViewModel.meetingData.endDate,
    });

  const onPressEsc = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    meetingViewModel.getSavedMembers();
    meetingViewModel.getSavedData();
    !membersStore.data.length && membersStore.getMembers();
    roomsStore.getRooms();
    document.addEventListener("keydown", onPressEsc);
    return () => document.removeEventListener("keydown", onPressEsc);
  }, []);

  useEffect(() => {
    refreshStatuses();
  }, [meetingViewModel.meetingData.startDate]);

  const onClose = () => meetingViewModel.resetMeetingData();

  const onDeleteMember = (userId: number) => {
    meetingViewModel.onUpdateMeeting(
      "members",
      meetingViewModel.meetingData.members.filter(
        (member) => userId !== member.id
      )
    );
  };

  const onDeleteMeeting = () => {
    meetingViewModel.deleteMeeting(meetingViewModel.meetingData.id).then(() => {
      onClose();
      //TODO: Постараться удалить повторные запросы митингов и раписания
      isMobile
        ? scheduleViewModel.getMeetingsFromStartDay()
        : calendarViewModel.getAllMeetings();
    });
  };

  const saveChanges = (action: "create" | "update") => {
    (action === "create"
      ? meetingViewModel.createMeeting()
      : meetingViewModel.updateMeeting()
    ).then((res) => {
      res && onClose();
      //TODO: Постараться удалить повторные запросы митингов и раписания
      isMobile
        ? scheduleViewModel.getMeetingsFromStartDay()
        : calendarViewModel.getAllMeetings();
    });
  };

  const onSaveMeeting = () => {
    if (!meetingViewModel.isMeetingValid) {
      setShowError(true);
      setOuterMembersError(true);
      return;
    }
    if (
      meetingViewModel.meetingData?.id &&
      meetingViewModel.meetingData?.intervalType !== "WITHOUT_REPEAT" &&
      meetingViewModel.isMeetingCycled
    ) {
      messageDialogService.openChange(false, intervalChanged, dateChanged);
    } else if (!meetingViewModel.meetingData?.id) {
      saveChanges("create");
    } else {
      saveChanges("update");
    }
  };

  const openConfirm = () => {
    if (meetingViewModel.meetingData.intervalType === "WITHOUT_REPEAT") {
      messageDialogService.open({
        message: "Вы уверены, что хотите удалить?",
        onAccept: onDeleteMeeting,
      });
    } else {
      messageDialogService.openChange(true);
    }
  };

  const onOpenMeetingPlaner = () => {
    meetingPlanerViewModel.setMembers(
      meetingViewModel.meetingData.members,
      meetingViewModel.meetingData?.meetingConfirmation
    );
    meetingPlanerViewModel.setDate(meetingViewModel.meetingData.startDate);
    meetingPlanerViewModel.setDraggingTime(
      meetingViewModel.meetingData.startDate,
      meetingViewModel.meetingData.endDate
    );
    navigate(PageRoutes.MEETING_PLANNER_PAGE);
  };

  const onDateUpdate = useCallback(
    (property: "startDate" | "endDate", value: Date) => {
      meetingViewModel.onUpdateMeeting(property, value);
      if (
        property === "endDate" &&
        value.getTime() >
          meetingViewModel.meetingData.meetingSetup.expDate.getTime()
      ) {
        meetingViewModel.onUpdateMeeting("meetingSetup", {
          ...meetingViewModel.meetingData.meetingSetup,
          expDate: value,
        });
      }
      setDateChanged(true);
    },
    []
  );

  const onUpdateFormat = (value: string) => {
    meetingViewModel.onUpdateMeeting("format", value);
    if (
      value === "OFFLINE" &&
      meetingViewModel.meetingData.repeatType === RepeatTypes.NEVER
    ) {
      meetingViewModel.onUpdateMeeting("repeatType", RepeatTypes.DATE);
    }
  };

  const onUpdateInterval = (
    property: "intervalType" | "repeatType" | "meetingSetup",
    value: string | TRepeatTypes | TMeetingSetup
  ) => {
    meetingViewModel.onUpdateMeeting(property, value);
    meetingViewModel.onUpdateMeeting(
      "modifyRecurringMeetingType",
      ModifyRecurringMeetingTypes.THIS_AND_SUBSEQUENT_MEETINGS
    );
    setIntervalChanged(true);
  };

  useEffect(() => {
    if (
      meetingViewModel.meetingData.format === Formats.OFFLINE &&
      meetingViewModel.meetingData.repeatType === RepeatTypes.NEVER
    ) {
      meetingViewModel.onUpdateMeeting("repeatType", RepeatTypes.DATE);
    }
  }, [meetingViewModel]);

  const openMobileMembersSelect = () => setShowMobileMemberSelect(true);

  const closeMobileMembersSelect = () => setShowMobileMemberSelect(false);

  const switchEditable = () =>
    meetingViewModel.setEditable(!meetingViewModel.isEdit);

  const onCreateOuterMember = () => {
    setOuterMembersError(!meetingViewModel.isOuterMembersValid);
    meetingViewModel.isOuterMembersValid &&
      meetingViewModel.createOuterMembers();
  };

  const onEndMeeting = () => {
    meetingViewModel.endMeting().then(() => {
      onClose();
      //TODO: Постараться удалить повторные запросы митингов и раписания
      isMobile
        ? scheduleViewModel.getMeetingsFromStartDay()
        : calendarViewModel.getAllMeetings();
    });
  };

  return (
    <>
      {isMobile && showMobileMemberSelect ? (
        <MobileMembersSelectList
          onClickMember={meetingViewModel.updateSelectedMembers}
          onClose={closeMobileMembersSelect}
        />
      ) : (
        <div className={setClass("edit-meeting", isMobile && "mobile")}>
          {!isMobile && (
            <>
              {meetingViewModel.isOpenedFromFilterPage ? (
                <BigBlueArrowLeft onClick={onClose} />
              ) : (
                <Cross onClick={onClose} divClassName="exit-button" />
              )}
            </>
          )}
          {isMobile &&
            meetingViewModel.isMeetingCanEdited &&
            !meetingViewModel.isEdit && (
              <div className="edit-mobile-header">
                <p className="edit-meeting-buton" onClick={switchEditable}>
                  Изменить
                </p>
                <DeleteMeetingIcon onClick={openConfirm} />
              </div>
            )}
          <div className={setClass("meeting-form", isMobile && "mobile")}>
            <NameBlock
              isEdit={meetingViewModel.isEdit}
              name={meetingViewModel.meetingData.meetingName}
              showError={showError}
              onCreateMeeting={onSaveMeeting}
              onUpdateMeeting={meetingViewModel.onUpdateMeeting}
              saveDisabled={!meetingViewModel.isMeetingUpdated}
            />
            {isMobile ? (
              <MobileDateBlock
                isEdit={meetingViewModel.isEdit}
                onUpdateMeeting={onDateUpdate}
                showError={showError}
              />
            ) : (
              <DateBlock
                isEdit={meetingViewModel.isEdit}
                onUpdateMeeting={onDateUpdate}
                showError={showError}
              />
            )}
            {meetingViewModel.meetingData.calendarId === -1 && (
              <>
                <IntervalBlock
                  startDate={meetingViewModel.meetingData.startDate}
                  isEdit={meetingViewModel.isEdit}
                  interval={meetingViewModel.meetingData.intervalType}
                  repeatType={meetingViewModel.meetingData.repeatType}
                  meetingSetup={meetingViewModel.meetingData.meetingSetup}
                  minDate={meetingViewModel.meetingData.endDate}
                  onUpdateMeeting={onUpdateInterval}
                  showError={showError}
                  meetingFormat={meetingViewModel.meetingData.format}
                />
                {!isMobile && (
                  <div className="info-block">Сведения о мероприятии</div>
                )}
                <FormatBlock
                  isEdit={meetingViewModel.isEdit}
                  format={meetingViewModel.meetingData.format}
                  onUpdateFormat={onUpdateFormat}
                />
                <MembersBlock
                  isEdit={meetingViewModel.isEdit}
                  meetingMembers={meetingViewModel.meetingData.members}
                  confirmations={
                    meetingViewModel.meetingData.meetingConfirmation
                  }
                  initiatorId={meetingViewModel.meetingData.initiator?.id}
                  onDeleteMember={onDeleteMember}
                  onClickMember={meetingViewModel.updateSelectedMembers}
                  format={meetingViewModel.meetingData.format}
                  handleMobileMembersSelect={openMobileMembersSelect}
                />
                {/* <OuterMembersBlock
                  isEdit={meetingViewModel.isEdit}
                  showError={outerMembersError}
                  outerMembersList={meetingViewModel.meetingData.outerMembers}
                  confirmations={meetingViewModel.meetingData.outerMemberConfirmation}
                  onCreateMember={onCreateOuterMember}
                  onChangeMember={meetingViewModel.getChangeOuterMembersFunctions}
                  onDeleteMember={meetingViewModel.deleteOuterMember}
                /> */}
                <LocationBlock
                  isEdit={meetingViewModel.isEdit}
                  format={meetingViewModel.meetingData.format}
                  room={meetingViewModel.meetingData.room}
                  link={meetingViewModel.meetingData.link}
                  rooms={roomsStore.data}
                  onUpdateMeeting={meetingViewModel.onUpdateMeeting}
                  showError={showError}
                />
                <DescriptionBlock
                  isEdit={meetingViewModel.isEdit}
                  description={meetingViewModel.meetingData.description}
                  disabled={isEditByModerator(meetingViewModel.meetingData)}
                  onUpdateMeeting={meetingViewModel.onUpdateMeeting}
                  showError={showError}
                />
              </>
            )}
            {meetingViewModel.isEdit && (
              <div className="area mobile-block">
                {isMobile ? (
                  <div className="mobile-button-block">
                    <Button
                      title="Отмена"
                      className="cancel"
                      onClick={onClose}
                    />
                    <Button
                      title="Сохранить"
                      disabled={!meetingViewModel.isMeetingUpdated}
                      onClick={onSaveMeeting}
                    />
                  </div>
                ) : (
                  <>
                    <Button
                      title="Найти время"
                      className="open-planer-button outlined"
                      onClick={onOpenMeetingPlaner}
                    />
                    {meetingViewModel.meetingData.id && (
                      <Button
                        title="Удалить встречу"
                        className="delete-meeting-button outlined"
                        onClick={openConfirm}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            {!meetingViewModel.isEdit &&
              meetingViewModel.isMeetingGoing &&
              AuthService.getUserId() ===
                meetingViewModel.meetingData.initiator?.id && (
                <Button
                  title="Завершить встречу"
                  className="delete-meeting-button outlined"
                  onClick={onEndMeeting}
                />
              )}
          </div>
          {isMobile && !meetingViewModel.isEdit && (
            <Button
              onClick={onClose}
              className="fixed-button mobile-close-button"
              icon={<Cross />}
            />
          )}
        </div>
      )}
    </>
  );
});
